// Admin API service for super-admin operations

import { apiClient, ApiResponse } from './api'

// Types for admin operations
export interface AdminAccount {
  id: string
  name: string
  createdAt: string
  subscription: {
    status: 'trialing' | 'active' | 'cancelled' | 'expired'
    tier: 'trial' | 'starter' | 'pro' | 'enterprise'
    trialEndsAt?: string
    trialExtendedUntil?: string
    source?: 'stripe' | 'invoice' | 'trial'
    customLimits?: Record<string, number>
  }
  workspaces?: number
  users?: number
}

interface UsageMetric {
  used: number
  limit: number
  percentage: number
}

export interface AdminAccountDetail extends AdminAccount {
  usage?: {
    conversations: UsageMetric
    messages: UsageMetric
    products: UsageMetric
    profiles: UsageMetric
  }
  workspaceList?: Array<{
    id: string
    name: string
    mode: string
    shopifyDomain?: string
  }>
}

export interface AuditLogEntry {
  auditId: string
  action: string
  adminEmail: string
  targetAccountId?: string
  timestamp: string
  details?: Record<string, unknown>
}

export interface ExtendTrialRequest {
  days: number
  reason: string
}

export interface ImpersonateRequest {
  reason: string
}

export interface ImpersonationResult {
  token: string
  expiresAt: string
  accountId: string
  accountName: string
}

export interface ActivateEnterpriseRequest {
  price: number // 0 for comped accounts
  customLimits?: {
    conversations?: number
    messages?: number
    profiles?: number
    products?: number
  }
  activationReason: string
}

// Admin API methods
export const adminApi = {
  /**
   * List all accounts (super-admin only)
   */
  async listAccounts(params?: {
    status?: string
    tier?: string
    search?: string
    limit?: number
    cursor?: string
  }): Promise<ApiResponse<{ accounts: AdminAccount[]; nextCursor?: string }>> {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.tier) searchParams.set('tier', params.tier)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.cursor) searchParams.set('cursor', params.cursor)

    const query = searchParams.toString()
    return apiClient.get(`/admin/accounts${query ? `?${query}` : ''}`)
  },

  /**
   * Get detailed account information
   */
  async getAccount(accountId: string): Promise<ApiResponse<AdminAccountDetail>> {
    return apiClient.get(`/admin/accounts/${accountId}`)
  },

  /**
   * Extend trial for an account
   */
  async extendTrial(accountId: string, request: ExtendTrialRequest): Promise<ApiResponse<{ newTrialEndsAt: string }>> {
    return apiClient.post(`/admin/accounts/${accountId}/extend-trial`, request)
  },

  /**
   * Create impersonation token for an account
   */
  async impersonate(accountId: string, request: ImpersonateRequest): Promise<ApiResponse<ImpersonationResult>> {
    return apiClient.post(`/admin/accounts/${accountId}/impersonate`, request)
  },

  /**
   * Get audit log entries
   */
  async getAuditLog(params?: {
    limit?: number
    cursor?: string
  }): Promise<ApiResponse<{ entries: AuditLogEntry[]; nextCursor?: string }>> {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.cursor) searchParams.set('cursor', params.cursor)

    const query = searchParams.toString()
    return apiClient.get(`/admin/audit-log${query ? `?${query}` : ''}`)
  },

  /**
   * Activate enterprise tier for an account (uses existing subscription endpoint)
   * This is the "comp account" functionality - set price: 0 for free enterprise access
   */
  async activateEnterprise(accountId: string, request: ActivateEnterpriseRequest): Promise<ApiResponse<{ subscription: unknown }>> {
    return apiClient.post('/subscriptions/enterprise', {
      accountId,
      customLimits: request.customLimits,
      reason: request.activationReason,
    }, {
      'x-account-id': accountId,
    })
  },
}
